import {
  getContentType,
  getContentTypeCategory,
} from '@carboncopyco/editor-utils';
import { ShowOutlineIcon } from '@carboncopyco/icons';
import { useMemo, useState } from 'react';
import { JsonForm } from '../../../JsonForm';
import {
  StaticResponseField,
  type StaticResponseFieldProps,
} from '../../../TransactionRuleForm';
import { useEditTransactionRuleContext } from '../../EditTransactionRuleContext';

export type EditStaticResponseProps = Pick<
  StaticResponseFieldProps,
  'onError' | 'height'
> & {
  showRepresentationTypeToggle?: boolean;
};

export const EditStaticResponse = ({
  onError,
  height = 420,
  showRepresentationTypeToggle,
}: EditStaticResponseProps) => {
  const { form } = useEditTransactionRuleContext();

  const response = form.watch('response');
  const representationFields = form.watch('representationFields');
  const representationTypeField = form.watch('representationType');

  const [representationType, setRepresentationType] = useState(
    representationTypeField
  );
  const headers = form.watch('headers');
  const contentType = getContentType(headers ?? []);
  const contentTypeCategory = getContentTypeCategory(contentType);
  const isRepresentationTypeToggleEnabled =
    showRepresentationTypeToggle &&
    contentTypeCategory === 'json' &&
    !!representationFields?.length;

  const json = useMemo(() => {
    try {
      return JSON.parse(response);
    } catch {
      return {};
    }
  }, [response]);

  const toggleRepresentationType = () => {
    setRepresentationType((prev) => (prev === 'raw' ? 'form' : 'raw'));
  };

  return (
    <div className="flex flex-1 flex-col min-h-0 min-w-0">
      {isRepresentationTypeToggleEnabled && (
        <div className="self-end flex gap-1.5 items-center text-sm cursor-pointer text-ds-primary-300 pb-1.5">
          <span>
            <ShowOutlineIcon size={20} />
          </span>
          <span onClick={toggleRepresentationType}>{`Switch to ${
            representationType === 'raw' ? 'form' : 'JSON'
          } view`}</span>
        </div>
      )}
      {representationType === 'raw' ? (
        <StaticResponseField
          height={height}
          onError={onError}
          hideLabel
        />
      ) : (
        <div className="overflow-auto">
          <JsonForm
            value={{ json, fields: representationFields }}
            onChange={({ json }) => {
              form.setValue('response', JSON.stringify(json), {
                shouldDirty: true,
              });
              form.trigger();
            }}
            mode="presentation"
          />
        </div>
      )}
    </div>
  );
};
